import React from 'react';
import { ContextMenuState } from 'src/types/ViewModel/Vessel/TankPlan';
import { useStowageStore } from 'src/context/StowageContext';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { useAuthStore } from 'src/context/AuthContext';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import OdfjellContextMenu from 'src/components/ui/odfjellContextMenu/OdfjellContextMenu';
import { isValidPortCall } from 'src/utils/stowage/stowageRotationUtils';

interface MenuItem {
    isSeparator?: boolean;
    content: React.ReactNode;
    isDisabled: boolean;
    onClick?: () => void;
    className?: string;
}

interface OperationListContextMenuProps {
    contextMenuState: ContextMenuState;
}

const BerthOperationListContextMenu: React.FC<OperationListContextMenuProps> = ({ contextMenuState }) => {
    const { activeStowagePlan, stowagePlans, setBerthOperationListContextMenuState, setIsCargoOperationPanelOpen } = useStowageStore();
    const { role } = useAuthStore();

    const closeContextMenu = () => {
        setBerthOperationListContextMenuState({ visible: false, cargoContents: [] } as ContextMenuState);
    };

    const handleOperation = (operation: () => void) => {
        if (!stowagePlans || !activeStowagePlan) {
            closeContextMenu();
            return;
        }
        operation();
        closeContextMenu();
    };

    const onNewCargoOperation = () => {
        setIsCargoOperationPanelOpen({ cargoOperation: null, berthCall: contextMenuState.berthCall, visible: true });
    };

    const canAddCargoOperation = () => {
        return contextMenuState?.berthCall?.portCall && isValidPortCall(contextMenuState?.berthCall?.portCall);
    };

    const menuItemConfigs = [
        {
            content: 'Add new cargo operation',
            permission: stowageFeatureEnum.EditCargo,
            isEnabled: () => canAddCargoOperation(),
            action: () => onNewCargoOperation(),
        },
    ];

    const createMenuItems = (): MenuItem[] =>
        menuItemConfigs.map((config) => ({
            isSeparator: false,
            content: config.content,
            isDisabled: !(hasPermission(role, config.permission) && config.isEnabled?.()),
            onClick: config.action ? () => handleOperation(config.action) : undefined,
            className: null,
        }));

    const menuItems = createMenuItems();

    return (
        <>
            {menuItems.length > 0 && (
                <OdfjellContextMenu
                    position={{ x: contextMenuState.x, y: contextMenuState.y }}
                    menuItems={createMenuItems()}
                    dataContextMenu="berth-operation-list"
                    className="berth-operation-list-context-menu"
                />
            )}
        </>
    );
};

export default React.memo(BerthOperationListContextMenu);
